import Logo from '@/images/logo.svg';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { ErrorIcon } from '@/utils/helpers/icons.helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--large-padding) var(--default-padding);
  overflow: hidden;
  background-color: var(--white-main);
  text-align: center;
`;

const StyledWatermark = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 105%;
  aspect-ratio: 140 / 27;
  transform: translateX(-50%);
  background-color: var(--purple-deepest);
  opacity: 0.05;
  -webkit-mask: url(${Logo}) no-repeat center / contain;
  mask: url(${Logo}) no-repeat center / contain;
  pointer-events: none;
`;

const StyledContent = styled(FlexContainer)`
  position: relative;
  max-width: 613px;
`;

const StyledIcon = styled.div`
  svg {
    width: 108px;
    height: 108px;
  }

  svg path {
    fill: var(--gray-darker);
    stroke: var(--gray-darker);
  }
`;

const StyledTitle = styled.h1`
  /* No matching typography token — Figma: 40px / 1.3 */
  margin: 0;
  font-size: 40px;
  line-height: 1.3;
  font-weight: 700;
  color: var(--black-main);
`;

const StyledDescription = styled.p`
  margin: 0;
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);
  color: var(--black-main);
`;

const StyledErrorCode = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--gray-darker);
`;

const ACTIONABLE_ERROR_DESCRIPTIONS: Record<string, string> = {
  different_user_authenticated: 'auth.signIn.error.differentUser',
  already_logged_in: 'auth.signIn.error.differentUser',
};

type SignInErrorPageProps = {
  onTryAgain: () => void;
  errorCode?: string | null;
};

export const SignInErrorPage: React.FC<SignInErrorPageProps> = ({
  onTryAgain,
  errorCode,
}) => {
  const { t } = useTranslation();

  const descriptionKey =
    (errorCode && ACTIONABLE_ERROR_DESCRIPTIONS[errorCode]) ||
    'auth.signIn.error.description';

  return (
    <StyledPage>
      <StyledWatermark aria-hidden="true" />

      <StyledContent direction="column" align="center" gap="var(--big-padding)">
        <FlexContainer
          direction="column"
          align="center"
          gap="var(--small-padding)"
        >
          <StyledIcon>
            <ErrorIcon />
          </StyledIcon>

          <FlexContainer
            direction="column"
            align="center"
            gap="var(--medium-big-padding)"
          >
            <StyledTitle>{t('auth.signIn.error.title')}</StyledTitle>
            <StyledDescription>{t(descriptionKey)}</StyledDescription>
            {errorCode && (
              <StyledErrorCode>
                {t('auth.signIn.error.code', { code: errorCode })}
              </StyledErrorCode>
            )}
          </FlexContainer>
        </FlexContainer>

        <AppButton
          label={t('auth.signIn.error.tryAgain')}
          onClick={onTryAgain}
        />
      </StyledContent>
    </StyledPage>
  );
};
